import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setMessages,
  markMessagesAsRead,
  clearConversationUnread,
} from "../Slices/ChatSlice";
import { callApi } from "../common/Apicall";

const useGetMessages = (pageNumber, receiverId, selectedConversationType) => {
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const loadedConversation = useRef(null);
  const URL = process.env.REACT_APP_URL;
  const dispatch = useDispatch();
  const senderId = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId
  );
  const conversationKey = `${selectedConversationType}:${receiverId}:${senderId}`;

  const messages = useSelector((state) => state?.conversation?.messages || []);
  const connection = useSelector((state) => state.socket.socketConnection);
  const isChatModalOpen = useSelector((state) => state.conversation.isChatModalOpen);
  const unreadCount = useSelector((state) => {
    const list = selectedConversationType === "group"
      ? state.conversation.withMessagesGroups : state.conversation.withMessagesConversations;
    return Number(list?.find((item) => String(item.value) === String(receiverId))?.unreadCount || 0);
  });

  // Reconcile badges that arrive after the initial read (for example a stale
  // sidebar response), and retry failed reads on returning to the open chat.
  useEffect(() => {
    if (loadedConversation.current !== conversationKey || !isChatModalOpen || loading || !unreadCount || !receiverId || !senderId) return;
    let cancelled = false;
    let pending = false;
    const read = async () => {
      if (pending || cancelled || document.visibilityState === "hidden") return;
      pending = true;
      try {
        const group = selectedConversationType === "group";
        const result = await callApi(
          `${URL}/${group ? "GroupChat/MarkMessagesAsRead" : "Chats/MarkAllMessagesAsRead"}`,
          "POST", group ? { groupId: receiverId, userId: senderId }
            : { senderId: receiverId, receiverId: senderId },
        );
        if (result?.error) throw new Error(result.error);
        if (cancelled) return;
        dispatch(markMessagesAsRead({ readerId: senderId, targetUserId: receiverId, conversationType: selectedConversationType }));
        dispatch(clearConversationUnread({ conversationId: receiverId, conversationType: selectedConversationType }));
        setError(null);
        if (connection?.state === "Connected") {
          try { await connection.invoke("ReadMessages", String(receiverId), String(senderId)); }
          catch (receiptError) { console.error("Read receipt delivery failed:", receiptError); }
        }
      } catch (readError) {
        if (!cancelled) setError(readError.message);
      } finally { pending = false; }
    };
    void read();
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, [conversationKey, isChatModalOpen, loading, unreadCount, receiverId, senderId, selectedConversationType, URL, dispatch, connection]);

  useEffect(() => {
    let cancelled = false;
    const getMessages = async () => {
      if (!receiverId || !senderId) return;
      setLoading(true);
      setError(null);

      try {
        const pageSize = 15;
        let endpoint, res;

        if (selectedConversationType === "group") {
          endpoint = `${URL}/GroupChat/GetGroupMessages/${receiverId}/${senderId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
        } else {
          endpoint = `${URL}/Chats/GetMessages/${senderId}/${receiverId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
        }

        res = await callApi(endpoint, "GET");
        if (cancelled) return;
        if (res.error) throw new Error(res.error);

        setTotalPages(res.totalPages);

        // Update messages in reverse order (newest first)
        const formattedMessages = [...res?.messages].reverse();
        dispatch(setMessages(pageNumber === 1 ? formattedMessages : [...formattedMessages, ...messages]));

        // The first page may contain only our own/read messages while older
        // unread messages still contribute to the conversation badge.
        if (pageNumber === 1) {
          try {
            const group = selectedConversationType === "group";
            const result = await callApi(
              `${URL}/${group ? "GroupChat/MarkMessagesAsRead" : "Chats/MarkAllMessagesAsRead"}`,
              "POST",
              group ? { groupId: receiverId, userId: senderId }
                : { senderId: receiverId, receiverId: senderId },
            );
            if (result?.error) throw new Error(result.error);
            if (cancelled) return;
            dispatch(markMessagesAsRead({ readerId: senderId, targetUserId: receiverId, conversationType: selectedConversationType }));
            dispatch(clearConversationUnread({ conversationId: receiverId, conversationType: selectedConversationType }));
            if (connection?.state === "Connected") {
              try {
                await connection.invoke("ReadMessages", String(receiverId), String(senderId));
              } catch (receiptError) {
                console.error("Read receipt delivery failed:", receiptError);
              }
            }
          } catch (readError) {
            if (!cancelled) setError(readError.message);
            // Keep the successfully loaded messages and the unread badge if
            // persistence failed; the reconciliation effect retries it.
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching messages:", err);
        setError(err.message);
        // Only clear on the first page. Page 1 is the initial load for a
        // conversation, so keeping the old array there would leave the
        // *previous* conversation's messages on screen under the new
        // conversation's header. For pages > 1 (scroll-up pagination) the
        // already-loaded history belongs to this same conversation, so a
        // transient failure must not wipe what the user is reading — the
        // `error` returned below is what surfaces the failure.
        if (pageNumber === 1) {
          dispatch(setMessages([]));
        }
      } finally {
        if (!cancelled) {
          loadedConversation.current = conversationKey;
          setLoading(false);
        }
      }
    };

    getMessages();

    // Cleanup function
    return () => {
      cancelled = true;
    };
  }, [conversationKey, receiverId, senderId, pageNumber, URL, dispatch, selectedConversationType, connection]);

  return { loading, error, totalPages, messages };
};

export default useGetMessages;
